import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { RouteComponentProps } from 'react-router';

import { Graph } from '@vromo/api/graph';

import queryString from 'query-string';

import { css } from 'emotion';

import JobForm from './JobForm';
import { EntityList, Job, Site } from '@vromo/models';
import theme from '@vromo/ui/components/theme';
import ErrorModal from '../common/ErrorModal';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import esStrings from '../../../locales/es.json';
import itStrings from '../../../locales/it.json';
import Title from '@vromo/ui/components/Title';
import Button from '@vromo/ui/components/Button';
import { slideInUp } from '@vromo/ui/styles/animated';
import styled from '@emotion/styled';
import Tracking from '@vromo/analytics';
import { useToasts } from '@vromo/ui/components/Toasts';
import { localServer, prodBackend, secrets } from '@vromo/api';
import OverlayablePanel from '@vromo/ui/components/Overlay';
import { qaLabelJobRequestForm } from '../../../common-qa-labels/job-request-form';

const JobFormContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 600px;
    width: 100%;
    margin: 10px;
    padding: 40px;
    font-size: 1.1em;
    border-radius: 10px;
    box-shadow: 0 2px 5px 1px rgba(93, 93, 93, 0.5);
    background: #fff;
    animation: ${slideInUp} 1s ease;

    @media (max-width: 400px) {
        padding: 30px;
    }
`;

const FormButton = styled(Button)`
    flex: 1;
    margin: 5px;
`;

export default observer<RouteComponentProps<{ auth: string }>>(function JobRequest({ match }) {
    const { auth } = match.params;
    const host = queryString.parse(location.search).host || prodBackend;
    const graph = new Graph(host.toString(), auth);

    const {
        t,
        i18n: { language }
    } = useTranslation();
    const [site, setSite] = useState<Site | null>(null);
    const [siteExists, setSiteExists] = useState(true);
    const [country, setCountry] = useState<string>(language === 'en' ? 'us' : language);
    const [callingCode, setCallingCode] = useState<string>();
    const [currencyCode, setCurrencyCode] = useState('USD');
    const jobForm = useRef<JobForm>(null);
    const showToast = useToasts();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        i18next.addResourceBundle('es', 'translation', esStrings);
        i18next.addResourceBundle('it', 'translation', itStrings);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const site = await graph.limited.graph().get();
                setSite(site);
            } catch (err) {
                console.error(err);
                setSiteExists(false);
            }
        })();

        if (location.host !== localServer) {
            (async () => {
                const response = await fetch(secrets.ipstack);
                if (response.ok) {
                    const {
                        country_code,
                        location: { calling_code },
                        currency
                    } = await response.json();

                    setCountry(country_code);
                    setCallingCode(calling_code);
                    setCurrencyCode(currency.code);
                }
            })();
        }
    }, []);

    const createSiteJob = async () => {
        if (jobForm.current?.validate()) {
            setLoading(true);
            try {
                const postData = new EntityList(Job);
                postData.push(jobForm.current.submit());
                const {
                    items: [job]
                } = await graph.limited.graph().jobs().post(postData);
                jobForm.current?.reset();
                showToast(
                    t('jobRequestForm.success', {
                        name: job!.contact?.name || site?.contact?.name
                    }),
                    'success'
                );
                Tracking.track('Job Created');
            } catch (err) {
                console.error(err);
                showToast(t('jobRequestForm.error'), 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div
            className={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                min-height: 100vh;
                background-color: ${theme.primary};
            `}>
            {site !== null ? (
                <JobFormContainer>
                    <OverlayablePanel active={loading}>
                        <Title style={{ margin: 0, fontWeight: 600 }}>{site?.contact?.name}</Title>

                        <JobForm
                            ref={jobForm}
                            site={site}
                            country={country}
                            callingCode={callingCode}
                            currencyCode={currencyCode}
                        />

                        <div
                            className={css`
                                display: flex;
                                justify-content: stretch;
                                margin-top: 20px;
                            `}>
                            <FormButton onClick={() => jobForm.current?.reset()}>
                                {t('common.resetForm')}
                            </FormButton>
                            <FormButton data-testid={qaLabelJobRequestForm.createBtn} primary onClick={() => createSiteJob()}>
                                {t('jobRequestForm.createJob')}
                            </FormButton>
                        </div>
                    </OverlayablePanel>
                </JobFormContainer>
            ) : (
                <ErrorModal isOpen={!siteExists} />
            )}
        </div>
    );
});
